import React, { useEffect, useState } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import Page from "../General/Page";
import imgLogo from "../../assets/images/logo11.png";
import sign from "../../assets/images/sign.png";
import "../../assets/css/result-card.css";

const formatMark = (value) => {
  if (value === null || value === undefined || value === "") {
    return "\u00a0";
  }
  return value;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return Number(value).toFixed(2);
};

const PrintPublishedStudentResultCards = () => {
  const [loading, setLoading] = useState(false);
  const [resultCard, setResultCard] = useState(null);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Print Published Student Result Cards";

    const urlParams = new URLSearchParams(window.location.search);
    const resultCardId = parseInt(urlParams.get("ID"), 10) || 0;
    const classId = parseInt(urlParams.get("ClassId"), 10) || 0;
    const sectionId = parseInt(urlParams.get("SectionId"), 10) || 0;

    if (!resultCardId) {
      setError("Select a published result card.");
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "ResultCard/GetPublishedStudentResultCards",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey: localStorage.getItem("AccessKey"),
        UserID: localStorage.getItem("ID"),
        MemberType: localStorage.getItem("MemberType"),
        ID: resultCardId,
        Class_Id: classId,
        Section_Id: sectionId,
      },
    })
      .then((response) => {
        if (response.data.status_code === 1) {
          setResultCard(response.data.ResultCard || null);
          setStudents(response.data.ListofStudents || []);
          setError("");
        } else {
          setStudents([]);
          setError(response.data.status_message || "No published results found.");
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError("Network error while loading published result cards.");
      });
  }, []);

  const printDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const isCompositeCard = () => {
    if (!resultCard) {
      return false;
    }

    const includedComponents = (resultCard.Components || []).filter(
      (component) => component.Include_In_Total !== false
    );

    return (
      String(resultCard.Result_Type || "").toLowerCase() === "composite" ||
      includedComponents.length > 1
    );
  };

  const showCompositeColumn = isCompositeCard();

  const renderExamColumn = (comp, index, columnCount) => {
    const title = comp.Exam_Title || `Exam ${index + 1}`;
    const subjects = comp.ListofSubjects || [];
    const showSubjectNames = index === 0;
    const columnWidth = `${Math.floor(100 / Math.max(columnCount, 1))}%`;

    return (
      <td key={`${comp.Source_Exam_ID}-${index}`} style={{ width: columnWidth, verticalAlign: "top" }}>
        <table className="table-bordered">
          <thead style={{ textAlign: "center" }}>
            <tr className="greyback">
              <th colSpan={showSubjectNames ? 3 : 2}>{title}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="greyback">
              {showSubjectNames && (
                <td className="subject">
                  <strong>Subject</strong>
                </td>
              )}
              <td>
                <strong>Total Marks</strong>
              </td>
              <td>
                <strong>Obtained Marks</strong>
              </td>
            </tr>
            {subjects.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {showSubjectNames && <td>{row.Subject_Name}&nbsp;</td>}
                <td>{formatMark(row.Total_Marks)}</td>
                <td>{formatMark(row.Obtained_Marks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    );
  };

  const renderCompositeColumn = (item, columnCount) => {
    const composite = item.ListofCompositeSubjects || [];
    const columnWidth = `${Math.floor(100 / Math.max(columnCount, 1))}%`;
    const compositeTitle =
      resultCard?.Print_Title || resultCard?.Title || "Final Result";

    return (
      <td style={{ width: columnWidth, verticalAlign: "top" }}>
        <table className="table-bordered">
          <thead style={{ textAlign: "center" }}>
            <tr className="greyback">
              <th colSpan="2">{compositeTitle}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="greyback">
              <td className="marks">
                <strong>Total Marks</strong>
              </td>
              <td>
                <strong>Obtained Marks</strong>
              </td>
            </tr>
            {composite.map((row, index) => (
              <tr key={index}>
                <td>{formatMark(row.Total_Marks)}</td>
                <td>{formatMark(row.Obtained_Marks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    );
  };

  return (
    <>
      <style>{`
        body {
          margin: 0;
          background: #fff;
        }
        .published-student-result-cards-print {
          background: #fff;
        }
        @media print {
          .print-error-banner { display: none !important; }
          header, nav, .navbar, .page-head, .page-content > .container > .card.mb-3 {
            display: none !important;
          }
        }
      `}</style>
      <div className="published-student-result-cards-print">
      {error && (
        <div className="alert alert-warning m-3 print-error-banner">{error}</div>
      )}
      <Page orientation="portrait" size="a4">
        {loading ? (
          <Spinner animation="border" role="status" />
        ) : (
          students.map((item, index) => {
            let components = (item.ListofExamComponents || []).slice(0, 4);
            if (
              !showCompositeColumn &&
              components.length === 1 &&
              !(components[0].ListofSubjects || []).length &&
              (item.ListofCompositeSubjects || []).length
            ) {
              components = [
                {
                  ...components[0],
                  ListofSubjects: item.ListofCompositeSubjects,
                },
              ];
            }
            const markColumnCount =
              components.length + (showCompositeColumn ? 1 : 0);
            const positionColumns = showCompositeColumn
              ? [...components, { Exam_Title: resultCard?.Title || "Final Result", isFinal: true }]
              : components.slice(0, 1);

            return (
              <div className="subpage" key={item.Student_ID || index}>
                <div className="school-head">
                  <table>
                    <tbody>
                      <tr>
                        <td style={{ width: "20%" }}>
                          <img src={imgLogo} style={{ width: "116px" }} alt="" />
                        </td>
                        <td style={{ width: "80%" }}>
                          <h2
                            style={{
                              fontSize: "22px",
                              fontWeight: "bold",
                              textAlign: "center",
                            }}
                          >
                            DISTRICT JINNAH PUBLIC SCHOOL AND COLLEGE, M.B.DIN
                          </h2>
                          <h4
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                            }}
                          >
                            PROVISIONAL RESULT INTIMATION
                            <br />
                            {item.Result_Title ||
                              resultCard?.Print_Title ||
                              resultCard?.Title ||
                              ""}
                          </h4>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="student-info-header">
                  <table>
                    <tbody>
                      <tr>
                        <td style={{ width: "85%" }}>
                          <table>
                            <tbody>
                              <tr>
                                <td>
                                  <div className="option">
                                    <span className="label">Name:</span>
                                    <span className="value">{item.Student_Name}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="option">
                                    <span className="label">Father&apos;s Name:</span>
                                    <span className="value">{item.Father_Name}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="option">
                                    <span className="label">Grade:</span>
                                    <span className="value">{item.Grade || "-"}</span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <table>
                            <tbody>
                              <tr>
                                <td>
                                  <div className="option">
                                    <span className="label">Class / Section: </span>
                                    <span className="value">
                                      {item.Class_Name}/{item.Section_Name}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="option">
                                    <span className="label">Position:</span>
                                    <span className="value">
                                      {item.Composite_Position || "-"}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="option">
                                    <span className="label">Reg. No.</span>
                                    <span className="value">{item.Registration_No}</span>
                                  </div>
                                </td>
                                <td style={{ width: "150px" }}>
                                  <div className="option">
                                    <span className="label">Date:</span>
                                    <span className="value">{printDate}</span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                          <table>
                            <tbody>
                              <tr>
                                <td>
                                  <div className="option">
                                    <span className="label">Address:</span>
                                    <span className="value">{item.Permanent_Address}</span>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                        <td
                          style={{
                            width: "15%",
                            verticalAlign: "top",
                            textAlign: "right",
                          }}
                        >
                          <img
                            src={
                              config.StudentImagePath + item.Registration_No + ".jpg"
                            }
                            alt=""
                            style={{ width: "80px" }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="result-card-body">
                  <table>
                    <tbody>
                      <tr>
                        {components.map((comp, compIndex) =>
                          renderExamColumn(comp, compIndex, markColumnCount)
                        )}
                        {showCompositeColumn && renderCompositeColumn(item, markColumnCount)}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="feedback">
                  <table>
                    <tbody>
                      <tr>
                        <td style={{ width: "50%", verticalAlign: "top" }}>
                          <table className="table-bordered">
                            <tbody>
                              <tr className="thead greyback">
                                <th colSpan="5" style={{ textAlign: "center" }}>
                                  Your Child as a Learner
                                </th>
                              </tr>
                              <tr>
                                <td style={{ width: "200px" }}></td>
                                <td>
                                  <div className="smiley">
                                    <img src="images/smileys/rarely.png" alt="" />
                                    <span>Rarely</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="smiley">
                                    <img src="images/smileys/sometime.png" alt="" />
                                    <span>Sometimes</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="smiley">
                                    <img src="images/smileys/most.png" alt="" />
                                    <span>Mostly</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="smiley">
                                    <img src="images/smileys/always.png" alt="" />
                                    <span>Always</span>
                                  </div>
                                </td>
                              </tr>
                              {[
                                "Listens class attentively",
                                "Learns and asks questions",
                                "Responsible with homework",
                                "Works neatly and orderly",
                              ].map((label) => (
                                <tr key={label}>
                                  <td>{label}</td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              ))}
                              <tr className="thead greyback">
                                <th colSpan="5" style={{ textAlign: "center" }}>
                                  Your child&apos;s social and personal development
                                </th>
                              </tr>
                              {[
                                "Remains pleasant in Class",
                                "Behaves well with classmates",
                                "Respects his elders",
                                "Sensitive to other’s feelings",
                                "Hates the wrong doings",
                              ].map((label) => (
                                <tr key={label}>
                                  <td>{label}</td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          <table className="position-table">
                            <tbody>
                              <tr>
                                <td colSpan={positionColumns.length}>
                                  <h4
                                    style={{
                                      textAlign: "center",
                                      margin: "10pt 0 0 0",
                                      fontSize: "12pt",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Position in Class
                                  </h4>
                                </td>
                              </tr>
                              <tr>
                                {positionColumns.map((comp, compIndex) => (
                                  <td key={comp.isFinal ? "final" : comp.Source_Exam_ID}>
                                    <span>{comp.Exam_Title || `Exam ${compIndex + 1}`}</span>
                                    <div className="position">
                                      ({formatPercent(
                                        comp.isFinal ? item.Composite_Percent : comp.Exam_Percent
                                      )}%)
                                      {comp.isFinal
                                        ? item.Composite_Position || "-"
                                        : comp.Section_Position || "-"}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            </tbody>
                          </table>
                        </td>

                        <td
                          style={{
                            width: "50%",
                            verticalAlign: "top",
                            padding: "0 20pt",
                          }}
                        >
                          <ul className="stars-list">
                            <li>
                              <i className="ri-star-fill"></i>
                              <span>Experiencing Significent Difficulty</span>
                            </li>
                            <li>
                              <i className="ri-star-fill"></i>
                              <span>Managing Comfortably</span>
                            </li>
                            <li>
                              <i className="ri-star-fill"></i>
                              <span>Capable and Competent</span>
                            </li>
                            <li>
                              <i className="ri-star-fill"></i>
                              <span>Highly Capable and Competent</span>
                            </li>
                          </ul>

                          <table className="table-bordered learning-starts">
                            <thead>
                              <tr className="greyback">
                                <th colSpan="5">
                                  Your Child&apos;s Learning During 1st Semester
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>English / Urdu</td>
                                <td><i className="ri-star-fill"></i></td>
                                <td><i className="ri-star-fill"></i></td>
                                <td><i className="ri-star-fill"></i></td>
                                <td><i className="ri-star-fill"></i></td>
                              </tr>
                              {[
                                "Listening Comprehension",
                                "Oral Expression /Speaking",
                                "Reading",
                                "Written Expression",
                              ].map((label) => (
                                <tr key={label}>
                                  <td>{label}</td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          <div className="remarks">
                            <h4 style={{ margin: "0" }}>Teacher&apos;s Remarks</h4>
                            <div>
                              <div className="option">
                                <span className="value">&nbsp;</span>
                              </div>
                              <div className="option" style={{ marginTop: "5px" }}>
                                <span className="value">&nbsp;</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="rc-footer">
                  <table>
                    <tbody>
                      <tr>
                        <td style={{ width: "33%" }}>
                          <div className="option">
                            <span className="label">Prepared By:</span>
                            <span className="value"></span>
                          </div>
                        </td>
                        <td style={{ width: "33%" }}></td>
                        <td style={{ width: "33%" }}>
                          <h4>Principal</h4>
                          <img
                            src={sign}
                            style={{
                              width: "100px",
                              border: "0px",
                              float: "right",
                              marginTop: "-70px",
                              marginLeft: "-85px",
                              marginBottom: "0px",
                              marginRight: "0px",
                            }}
                            alt=""
                          />
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3" style={{ fontSize: "9pt", paddingTop: "10px" }}>
                          Note: This provisional result intimation is issued as a notice only.
                          Errors and omissions are accepted.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </Page>
      </div>
    </>
  );
};

export default PrintPublishedStudentResultCards;
